import { z } from "zod";
import { AI_EFFORTS, CLAUDE_MODELS, type ClaudeModelId } from "./ai-models";
import { ProjectFilesInput } from "./builder";
import { ProfileExtrasInput } from "./network";
import { INVITABLE_ROLES, ROLES } from "./roles";
import { TEMPLATES } from "./templates";

/** Schemas de entrada da API. Usados pelo servidor (validação) e pelo cliente (formulários). */

export const Handle = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])$/, "Use 3–32 letras minúsculas, números ou hífens.");

export const Visibility = z.enum(["private", "unlisted", "public"]);
export type Visibility = z.infer<typeof Visibility>;

export const LEVELS = ["iniciante", "junior", "pleno", "senior"] as const;
export const GOALS = ["aprender", "ensinar", "construir", "hackathon"] as const;

export const CreateProjectInput = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  template: z.enum(TEMPLATES).default("node"),
  visibility: Visibility.default("private"),
  groupId: z.uuid().optional(),
  /** Arquivos iniciais próprios (ex.: gerados pelo construtor com IA). Substituem os do template. */
  files: ProjectFilesInput.optional(),
  runtime: z.enum(["node", "python", "java"]).optional(),
  runCommand: z.string().trim().max(200).optional(),
});
export type CreateProjectInput = z.infer<typeof CreateProjectInput>;

export const UpdateProjectInput = z
  .object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).nullable(),
    visibility: Visibility,
    aiEnabled: z.boolean(),
    recordingEnabled: z.boolean(),
    showActivity: z.boolean(),
    runCommand: z.string().trim().max(500).nullable(),
  })
  .partial();
export type UpdateProjectInput = z.infer<typeof UpdateProjectInput>;

export const CreateInviteInput = z.object({
  role: z.enum(INVITABLE_ROLES).default("editor"),
  expiresInHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 30)
    .default(72),
  maxUses: z.number().int().min(1).max(500).default(10),
  /** Link sem validade e sem limite de usos (vale até ser revogado). */
  unlimited: z.boolean().default(false),
});
export type CreateInviteInput = z.infer<typeof CreateInviteInput>;

/**
 * Link "ilimitado": guardado com usos e validade no máximo (o banco exige os dois), e mostrado
 * como "sem limite" (ver {@link isUnlimitedInvite}).
 */
export const UNLIMITED_INVITE = { maxUses: 2_000_000_000, expiresAt: "9999-12-31T00:00:00.000Z" } as const;
export const isUnlimitedInvite = (i: { maxUses: number }) => i.maxUses >= UNLIMITED_INVITE.maxUses;

/** Troca de papel e/ou da permissão de ver os arquivos .env (o dono sempre vê). */
export const UpdateMemberInput = z
  .object({ role: z.enum(ROLES), canSeeEnv: z.boolean() })
  .partial()
  .refine((v) => v.role !== undefined || v.canSeeEnv !== undefined, "Nada para alterar.");

/**
 * Convite direto: para uma pessoa pelo nome, entre quem você segue ou quem segue você. Ela recebe
 * a notificação e decide se aceita. No projeto, "Acesso total" (dono) só vale por convite direto.
 */
export const DirectProjectInviteInput = z.object({
  userId: z.uuid(),
  role: z.enum(["owner", ...INVITABLE_ROLES]).default("editor"),
});
export const DirectGroupInviteInput = z.object({
  userId: z.uuid(),
  role: z.enum(["admin", "member"]).default("member"),
});
export const RespondInviteInput = z.object({ accept: z.boolean() });

export const HandoverInput = z.object({ toUserId: z.uuid() });

/** Link que pode virar `href`: só http(s), sem usuário/senha. Outro protocolo (`javascript:`) seria XSS. */
export function isWebUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const u = new URL(value);
    return (u.protocol === "https:" || u.protocol === "http:") && !u.username && !u.password;
  } catch {
    return false;
  }
}

const WebUrl = z.url().max(200).refine(isWebUrl, "Use um link http:// ou https://");

export const UpdateProfileInput = z
  .object({
    name: z.string().trim().min(1).max(80),
    handle: Handle,
    bio: z.string().trim().max(1000).nullable(),
    headline: z.string().trim().max(120).nullable(),
    level: z.enum(LEVELS).nullable(),
    goal: z.enum(GOALS).nullable(),
    languages: z.array(z.string().trim().min(2).max(10)).max(10),
    timezone: z.string().trim().max(64).nullable(),
    available: z.boolean(),
    // Só https: um link `javascript:` ou `data:` no perfil viraria XSS ao ser clicado.
    githubUrl: WebUrl.nullable(),
    websiteUrl: WebUrl.nullable(),
  })
  .partial()
  .extend(ProfileExtrasInput.shape);
export type UpdateProfileInput = z.infer<typeof UpdateProfileInput>;

export const UpdateSkillsInput = z.object({
  skills: z
    .array(z.object({ slug: z.string().trim().min(1).max(40), proficiency: z.number().int().min(1).max(5) }))
    .max(30),
});

export const DiscoverDevsQuery = z.object({
  q: z.string().trim().max(100).optional(),
  skill: z.string().trim().max(40).optional(),
  lang: z.string().trim().max(10).optional(),
  tz: z.string().trim().max(64).optional(),
  goal: z.enum(GOALS).optional(),
  level: z.enum(LEVELS).optional(),
  /** País (ISO 3166-1 alfa-2). */
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  /** Um título exato do perfil (ex.: "AppSec"). */
  title: z.string().trim().max(40).optional(),
  // z.coerce.boolean() transformaria "false" em true; aceitamos só os literais.
  available: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
  /** Só quem está online agora (com o app aberto). */
  online: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
  /** Várias habilidades (todas precisam estar no perfil): "webgl,react". */
  skills: z
    .string()
    .trim()
    .max(300)
    .transform((s) =>
      s
        .split(",")
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 6),
    )
    .optional(),
  sort: z.enum(["relevance", "online", "reputation", "recent"]).default("relevance"),
  limit: z.coerce.number().int().min(1).max(60).default(24),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
});
export type DiscoverDevsQuery = z.infer<typeof DiscoverDevsQuery>;

export const MatchQuery = z.object({
  skills: z
    .string()
    .trim()
    .max(400)
    .transform((s) =>
      s
        .split(",")
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean),
    )
    .optional(),
  limit: z.coerce.number().int().min(1).max(30).default(10),
});

export const CreateGroupInput = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(500).optional(),
  visibility: z.enum(["private", "public"]).default("private"),
});

export const CreateGroupInviteInput = z.object({
  role: z.enum(["admin", "member"]).default("member"),
  expiresInHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 30)
    .default(72),
  maxUses: z.number().int().min(1).max(500).default(25),
  unlimited: z.boolean().default(false),
});

export const CreateReportInput = z.object({
  targetType: z.enum(["user", "project", "group", "sandbox", "message"]),
  /** UUID (pessoa, projeto, grupo, sandbox) ou id numérico (mensagem do chat). */
  targetId: z.union([z.uuid(), z.string().regex(/^\d{1,18}$/)]),
  reason: z.string().trim().min(5).max(2000),
});

export const PeerReviewInput = z.object({
  revieweeId: z.uuid(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().trim().max(1000).optional(),
});

export const DevLoginInput = z.object({
  handle: Handle,
  name: z.string().trim().min(1).max(80),
});

const Email = z
  .string()
  .trim()
  .toLowerCase()
  .max(320)
  .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, "E-mail inválido.");

/** Entrar com e-mail e senha. */
export const PasswordLoginInput = z.object({
  email: Email,
  password: z.string().min(1, "Informe a senha.").max(128),
});

/** Criar conta com e-mail e senha. */
export const RegisterInput = z.object({
  name: z.string().trim().min(2, "Informe seu nome.").max(80),
  email: Email,
  password: z.string().min(8, "A senha precisa de pelo menos 8 caracteres.").max(128),
});

/** Código de 6 dígitos enviado por e-mail (confirmar o e-mail, trocar a senha). */
export const EmailCode = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s-]/g, ""))
  .pipe(z.string().regex(/^\d{6}$/, "O código tem 6 números."));

export const VerifyEmailInput = z.object({ email: Email, code: EmailCode });
export const EmailOnlyInput = z.object({ email: Email });
export const ResetPasswordInput = z.object({
  email: Email,
  code: EmailCode,
  password: z.string().min(8, "A senha precisa de pelo menos 8 caracteres.").max(128),
});

export const AiChatInput = z.object({
  projectId: z.uuid(),
  messages: z
    .array(z.object({ role: z.enum(["user", "assistant"]), content: z.string().min(1).max(20_000) }))
    .min(1)
    .max(40),
  activeFileId: z.string().max(64).optional(),
  selection: z.string().max(20_000).optional(),
  model: z.enum(CLAUDE_MODELS.map((m) => m.id) as [ClaudeModelId, ...ClaudeModelId[]]).optional(),
  effort: z.enum(AI_EFFORTS).optional(),
});
export type AiChatInput = z.infer<typeof AiChatInput>;

export const AiSummaryInput = z.object({ projectId: z.uuid() });

// ───────────── Segurança da conta ─────────────

/** Trocar a senha (contas criadas pelo GitHub/Google podem criar uma, sem a atual). */
export const ChangePasswordInput = z.object({
  current: z.string().max(128).optional(),
  next: z.string().min(8, "A senha precisa de pelo menos 8 caracteres.").max(128),
});

/** Código do app autenticador (6 dígitos) ou de recuperação ("abcd-ef23"). */
export const TwoFactorCodeInput = z.object({ code: z.string().trim().min(6).max(20) });

/** Segunda etapa do login: o bilhete devolvido pelo login e o código. */
export const TwoFactorLoginInput = z.object({
  ticket: z.string().min(20).max(200),
  code: z.string().trim().min(6).max(20),
});

export interface AccountSecurity {
  email: string;
  hasPassword: boolean;
  passwordChangedAt: string | null;
  twoFactor: { enabled: boolean; enabledAt: string | null; recoveryRemaining: number };
  /** Logins ligados à conta (github, google). */
  providers: string[];
}

/** Evento recente da conta (entradas, senha, duas etapas, sessões). */
export interface SecurityEvent {
  action: string;
  at: string;
  ip: string | null;
  meta: Record<string, unknown> | null;
}

/** Resposta do login quando a conta tem verificação em duas etapas. */
export interface TwoFactorChallenge {
  twoFactor: { ticket: string };
}
